<script setup lang="ts">
// Import Swiper Vue.js components
import { Swiper, SwiperSlide } from 'swiper/vue'
// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
// import required modules
import { Pagination } from 'swiper/modules'
import { computed, nextTick, onMounted, ref, watch, type PropType } from 'vue'
import { debounce } from 'perfect-debounce'
import { titleBar } from '@/composables/menu/title'

import type { ArticleAndMessageResponse } from '@/composables/life/life'
// script setup

const props = defineProps({
  serviceId: {
    type: String,
    required: true
  },
  fullName: {
    type: String,
    required: true
  },
  lifeAndArticle: {
    type: Object as PropType<ArticleAndMessageResponse | undefined>,
    required: true
  },
  canManage: {
    type: Boolean,
    default: true,
    required: true
  }
})

let innerWidth = ref(0)
if (import.meta.client) {
  innerWidth.value = window.innerWidth
}

const lifeAndArticle = computed(() => props.lifeAndArticle)

onMounted(() => {
  window.addEventListener('resize', (e) => debounced())
})

const debounced = debounce(() => {
  innerWidth.value = window.innerWidth
}, 100)

const slidesPerView = ref<number>(innerWidth.value < 768 ? 1 : 2)
const showSlide = ref(true)
const refreshSlide = async () => {
  showSlide.value = false
  await nextTick()
  showSlide.value = true
}

watch(
  () => innerWidth.value,
  (newVal, oldVal) => {
    if (newVal < 768) {
      slidesPerView.value = 1
      if (oldVal >= 768) {
        refreshSlide()
      }
    } else {
      slidesPerView.value = 2
      if (oldVal < 768) {
        refreshSlide()
      }
    }
  }
)

const emit = defineEmits(['showSetRealNameDialog'])
const paginationConfig = {
  enabled: true,
  clickable: true
} as any
</script>

<template>
  <ClientOnly>
    <swiper
      v-if="showSlide"
      :spaceBetween="30"
      :slides-per-view="slidesPerView"
      :pagination="paginationConfig"
      :modules="[Pagination]"
      pb-6
      w-full>
      <swiper-slide w-full>
        <AlbumSlide
          :name="props.fullName"
          :serviceId="props.serviceId"
          :canManage="props.canManage"
          :count="lifeAndArticle?.sectionAlbum?.total || 0"
          :photoList="lifeAndArticle?.sectionAlbum?.medias"
          @showSetRealNameDialog="emit('showSetRealNameDialog')" />
      </swiper-slide>

      <swiper-slide w-full>
        <LifetimeSlide
          :name="props.fullName"
          :serviceId="props.serviceId"
          :canManage="props.canManage"
          :article="lifeAndArticle?.sectionLifeArticle"
          @showSetRealNameDialog="emit('showSetRealNameDialog')" />
      </swiper-slide>

      <swiper-slide w-full>
        <StorySlide
          :name="props.fullName"
          :serviceId="props.serviceId"
          :canManage="props.canManage"
          :post="lifeAndArticle?.sectionMemorialPost"
          @showSetRealNameDialog="emit('showSetRealNameDialog')" />
      </swiper-slide>
    </swiper>
  </ClientOnly>
</template>

<style lang="scss">
.swiper-pagination-bullet {
  background: #e9e9e9;
  opacity: 0.5;
}

/* 当前激活的分页点 */
.swiper-pagination-bullet-active {
  background: #696969;
  /* 改为红色 */
  opacity: 1;
}

/* 增加分页器和幻灯片之间的间距 */
.swiper-pagination {
  position: absolute;
  bottom: -5px !important;
  /* 默认是10px，增大这个值 */
}

/* style */
</style>
